import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	AlertCircle,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	ChevronsUpDown,
	ChevronUp,
	Columns3,
	Download,
	Pencil,
	Plus,
	RefreshCw,
	RotateCcw,
	Rows3,
	Rows4,
	Search,
	Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { exportToCsv } from '~/lib/export-csv';

export type SortDir = 'asc' | 'desc';
type Density = 'comfortable' | 'compact';

export interface SystemGridColumn<T> {
	key: string;
	/** Already-translated header label. */
	header: string;
	/**
	 * Column sizing. A fixed track (e.g. '110px') is treated as both width and min-width; a
	 * flexible track ('1fr' / 'minmax(0,1fr)') becomes an auto column that absorbs slack with a
	 * sensible min-width. Columns overflow into horizontal scroll when they exceed the viewport.
	 */
	width: string;
	align?: 'left' | 'right' | 'center';
	/** Sort key extractor. Omit to make the column non-sortable. */
	sortValue?: (row: T) => string | number | null | undefined;
	render: (row: T) => ReactNode;
	/** CSV cell value. Omit to exclude the column from the Excel/CSV export. */
	exportValue?: (row: T) => string | number | null | undefined;
	/** Whether the column can be hidden via the column selector (default true). */
	toggleable?: boolean;
	/** Whether the column is shown by default. Set false for extra columns that stay available in the selector. */
	defaultVisible?: boolean;
}

/** Min-width baseline for flexible columns; fixed columns use their own px value. */
const FLEX_MIN_WIDTH = 180;
/** Pinned actions column width — fits the built-in edit+delete pair; widen via `actionsWidth` when `rowActions` adds more buttons. */
const ACTIONS_WIDTH = 84;
/** Flexible tracks (fr/minmax/auto) become auto `<col>`s that share slack; fixed px tracks keep their width. */
function colWidth(width: string): CSSProperties {
	if (/fr|minmax|auto/.test(width)) return {};
	return { width };
}
/** Per-column floor for the table's min-width, so it scrolls horizontally instead of crushing columns. */
function colMinWidth(width: string): number {
	if (/fr|minmax|auto/.test(width)) return FLEX_MIN_WIDTH;
	const px = parseInt(width, 10);
	return Number.isFinite(px) ? px : FLEX_MIN_WIDTH;
}

/**
 * Server-driven mode: search, sort and paging are owned by the caller (a server-paged query) and the
 * grid only renders `rows` and reports interactions. Used by grids over tables that grow without
 * bound, e.g. the activity log. Omit for the default client-side behaviour.
 */
export interface SystemGridServerMode {
	/** Total rows matching the current query on the server (drives the pager and the range label). */
	totalCount: number;
	pageIndex: number;
	pageSize: number;
	onPageChange: (pageIndex: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	sort: { key: string; dir: SortDir } | null;
	onSortChange: (sort: { key: string; dir: SortDir } | null) => void;
	search: string;
	onSearchChange: (search: string) => void;
}

interface SystemGridProps<T> {
	/** Stable id used to persist column visibility (localStorage key). */
	gridId: string;
	title: string;
	/** One line under the heading, for a grid whose subject needs explaining. Ignored in embedded mode. */
	subtitle?: string;
	rows: T[];
	loading: boolean;
	error?: boolean;
	onRetry?: () => void;
	getRowId: (row: T) => number | string;
	columns: SystemGridColumn<T>[];
	/** Free-text search predicate over a row. Omit in server mode — the server does the filtering. */
	searchPredicate?: (row: T, query: string) => boolean;
	/** Present when search/sort/paging are server-driven; see {@link SystemGridServerMode}. */
	server?: SystemGridServerMode;
	/** Extra toolbar controls (e.g. server-side filter selects) rendered next to the column selector. */
	toolbarExtras?: ReactNode;
	defaultSort?: { key: string; dir: SortDir };
	emptyIcon: ReactNode;
	/** Add button — omit (with onAdd) for a read-only grid (e.g. audit logs). */
	addLabel?: string;
	onAdd?: () => void;
	/** Row edit/delete actions — omit both for a read-only grid (the actions column collapses). */
	onEdit?: (row: T) => void;
	onDelete?: (row: T) => void;
	/** Rows that offer delete (default: all, when onDelete is set). */
	canDelete?: (row: T) => boolean;
	/** Makes the whole row clickable (e.g. open the detail page). Clicks inside the actions column don't trigger it. */
	onRowClick?: (row: T) => void;
	/** Optional extra per-row actions rendered before edit/delete (e.g. run, revoke). */
	rowActions?: (row: T) => ReactNode;
	/** Width of the pinned actions column (px). Set when `rowActions` adds buttons beyond edit+delete, so they don't overlap the last data column. Default 84. */
	actionsWidth?: number;
	/** CSV file name (without extension). */
	exportFilename: string;
	/**
	 * Embedded mode (e.g. rendered inside a dialog that already supplies its own heading): drops the
	 * page-head row entirely and folds the export/add actions into the search+columns toolbar row.
	 */
	embedded?: boolean;
}

const PAGE_SIZES = [25, 50, 100];

/** Default visibility per toggleable column key (honours each column's `defaultVisible`). */
function defaultVisibility(cols: { key: string; defaultVisible?: boolean }[]): Record<string, boolean> {
	return Object.fromEntries(cols.map(c => [c.key, c.defaultVisible !== false]));
}

function loadVisibility(gridId: string, cols: { key: string; defaultVisible?: boolean }[]): Record<string, boolean> {
	const base = defaultVisibility(cols);
	try {
		const raw = globalThis.localStorage?.getItem(`admin-v2:${gridId}:columns`);
		if (!raw) return base;
		const parsed = JSON.parse(raw) as Record<string, boolean>;
		for (const c of cols) if (typeof parsed[c.key] === 'boolean') base[c.key] = parsed[c.key];
	} catch {
		// Corrupt/blocked storage → fall back to per-column defaults.
	}
	return base;
}

/** Compact page-number list with ellipses (mirrors the products grid pager). */
function pageNumbers(current: number, pageCount: number): (number | 'gap')[] {
	if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
	const out: (number | 'gap')[] = [0];
	const start = Math.max(1, current - 1);
	const end = Math.min(pageCount - 2, current + 1);
	if (start > 1) out.push('gap');
	for (let i = start; i <= end; i++) out.push(i);
	if (end < pageCount - 2) out.push('gap');
	out.push(pageCount - 1);
	return out;
}

/**
 * Shared CRUD grid for the System pages. Encapsulates the products-grid chrome — toolbar panel
 * (search + column selector), grid bar ("Εμφάνιση X–Y από Z" + density + refresh), sortable
 * headers, the products-style pager, and Excel/CSV export — driven by a column-definition array.
 */
export function SystemGrid<T>({
	gridId,
	title,
	subtitle,
	rows,
	loading,
	error,
	onRetry,
	getRowId,
	columns,
	searchPredicate,
	server,
	toolbarExtras,
	defaultSort,
	emptyIcon,
	addLabel,
	onAdd,
	onEdit,
	onDelete,
	canDelete,
	onRowClick,
	rowActions,
	actionsWidth = ACTIONS_WIDTH,
	exportFilename,
	embedded
}: SystemGridProps<T>) {
	const { t } = useTranslation();
	const hasRowActions = Boolean(onEdit || onDelete || rowActions);

	const toggleableCols = useMemo(() => columns.filter(c => c.toggleable !== false), [columns]);
	const [visible, setVisible] = useState<Record<string, boolean>>(() => loadVisibility(gridId, toggleableCols));
	const [density, setDensity] = useState<Density>('comfortable');
	const [localSearch, setLocalSearch] = useState('');
	const [localSort, setLocalSort] = useState<{ key: string; dir: SortDir } | null>(defaultSort ?? null);
	const [localPageIndex, setLocalPageIndex] = useState(0);
	const [localPageSize, setLocalPageSize] = useState(PAGE_SIZES[0]);

	// In server mode the caller owns search/sort/paging; the grid keeps rendering from the same fields.
	const search = server ? server.search : localSearch;
	const setSearch = server ? server.onSearchChange : setLocalSearch;
	const sort = server ? server.sort : localSort;
	const pageIndex = server ? server.pageIndex : localPageIndex;
	const setPageIndex = server ? server.onPageChange : setLocalPageIndex;
	const pageSize = server ? server.pageSize : localPageSize;
	const setPageSize = server ? server.onPageSizeChange : setLocalPageSize;

	const persistVisibility = useCallback(
		(next: Record<string, boolean>) => {
			try {
				globalThis.localStorage?.setItem(`admin-v2:${gridId}:columns`, JSON.stringify(next));
			} catch {
				// Storage unavailable — visibility stays in-memory.
			}
		},
		[gridId]
	);

	const toggleColumn = (key: string) =>
		setVisible(prev => {
			const next = { ...prev, [key]: !prev[key] };
			persistVisibility(next);
			return next;
		});
	const resetColumns = () => {
		const next = defaultVisibility(toggleableCols);
		persistVisibility(next);
		setVisible(next);
	};

	const visibleColumns = useMemo(() => columns.filter(c => c.toggleable === false || visible[c.key] !== false), [columns, visible]);

	// table-layout: fixed needs an explicit min-width so columns keep their configured size and the
	// container scrolls horizontally, instead of the browser crushing columns to fit the viewport.
	const tableMinWidth = useMemo(
		() => visibleColumns.reduce((sum, c) => sum + colMinWidth(c.width), 0) + (hasRowActions ? actionsWidth : 0),
		[visibleColumns, hasRowActions, actionsWidth]
	);

	// asc → desc → unsorted
	const nextSort = (prev: { key: string; dir: SortDir } | null, key: string) => {
		if (prev?.key !== key) return { key, dir: 'asc' as SortDir };
		if (prev.dir === 'asc') return { key, dir: 'desc' as SortDir };
		return null;
	};
	const cycleSort = (key: string) => (server ? server.onSortChange(nextSort(server.sort, key)) : setLocalSort(prev => nextSort(prev, key)));

	// In server mode `rows` is already the filtered, sorted page — both pipelines below are skipped.
	const filtered = useMemo(() => {
		if (server || !searchPredicate) return rows;
		const q = search.trim().toLowerCase();
		return q ? rows.filter(r => searchPredicate(r, q)) : rows;
	}, [rows, search, searchPredicate, server]);

	const sorted = useMemo(() => {
		if (server || !sort) return filtered;
		const col = columns.find(c => c.key === sort.key);
		if (!col?.sortValue) return filtered;
		const getVal = col.sortValue;
		const dir = sort.dir === 'asc' ? 1 : -1;
		return [...filtered].sort((a, b) => {
			const va = getVal(a);
			const vb = getVal(b);
			if (va == null && vb == null) return 0;
			if (va == null) return 1;
			if (vb == null) return -1;
			if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
			return String(va).localeCompare(String(vb)) * dir;
		});
	}, [filtered, sort, columns, server]);

	const total = server ? server.totalCount : sorted.length;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	// Clamp the page when filtering/sorting shrinks the result set below the current page. Skipped in
	// server mode: the caller owns the page index and clamps it against the total it fetched.
	useEffect(() => {
		if (!server && pageIndex > pageCount - 1) setPageIndex(pageCount - 1);
	}, [pageIndex, pageCount, server, setPageIndex]);

	const pageRows = useMemo(
		() => (server ? sorted : sorted.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)),
		[sorted, pageIndex, pageSize, server]
	);
	const from = total === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, total);

	const cellPadY = density === 'compact' ? 'py-1.5' : 'py-2.5';
	const colSpan = visibleColumns.length + (hasRowActions ? 1 : 0);
	// Sticky column chrome: identity (first) pinned left, actions pinned right.
	const stickyLeft = 'sticky left-0 z-[1] border-r border-border';
	const stickyRight = 'sticky right-0 z-[1] border-l border-border';

	// In server mode only the loaded page is in memory, so the export covers the current page.
	const onExport = () => {
		const exportCols = columns.filter(c => c.exportValue);
		exportToCsv(
			exportFilename,
			exportCols.map(c => ({ header: c.header, value: c.exportValue! })),
			server ? pageRows : sorted
		);
	};

	if (error) {
		return (
			<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface py-16 text-center">
				<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
				<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
				{onRetry && (
					<Button variant="outline" onClick={onRetry}>
						{t('common.retry')}
					</Button>
				)}
			</div>
		);
	}

	const actionButtons = (
		<>
			<Button variant="outline" onClick={onExport} disabled={loading || total === 0}>
				<Download size={16} /> {t('system.grid.export')}
			</Button>
			{onAdd && (
				<Button onClick={onAdd}>
					<Plus size={16} /> {addLabel}
				</Button>
			)}
		</>
	);

	return (
		<div className="flex flex-col gap-3">
			{/* Page head — omitted in embedded mode (the host dialog supplies its own heading; the
			    export/add actions move into the toolbar row below). */}
			{!embedded && (
				<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{title}</h1>
						{subtitle && <p className="mt-1 text-sm text-foreground-muted">{subtitle}</p>}
					</div>
					<div className="flex items-center gap-2">{actionButtons}</div>
				</div>
			)}

			{/* Toolbar panel: search + column selector */}
			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={search}
						onChange={e => {
							setSearch(e.target.value);
							setPageIndex(0);
						}}
						placeholder={t('common.search')}
						className="pl-8"
						aria-label={t('common.search')}
					/>
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline">
							<Columns3 size={16} /> {t('system.grid.columns')}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56">
						<DropdownMenuLabel>{t('system.grid.columnsTitle')}</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{columns
							.filter(c => c.toggleable !== false)
							.map(c => (
								<DropdownMenuCheckboxItem
									key={c.key}
									checked={visible[c.key] !== false}
									onSelect={e => e.preventDefault()}
									onCheckedChange={() => toggleColumn(c.key)}>
									{c.header}
								</DropdownMenuCheckboxItem>
							))}
						<DropdownMenuSeparator />
						<DropdownMenuItem onSelect={resetColumns}>
							<RotateCcw size={14} /> {t('system.grid.columnsReset')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				{toolbarExtras}
				{embedded && <div className="ml-auto flex items-center gap-2">{actionButtons}</div>}
			</div>

			{/* Grid bar: range + density + refresh */}
			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('system.grid.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{from}–{to}
					</strong>{' '}
					{t('system.grid.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<div className="flex items-center gap-2">
					<div className="inline-flex rounded-sm border border-border bg-surface p-0.5">
						<button
							type="button"
							title={t('system.grid.comfortable')}
							onClick={() => setDensity('comfortable')}
							className={cn(
								'rounded-[3px] px-1.5 py-1',
								density === 'comfortable' ? 'bg-muted text-foreground' : 'text-foreground-subtle'
							)}>
							<Rows3 size={13} />
						</button>
						<button
							type="button"
							title={t('system.grid.compact')}
							onClick={() => setDensity('compact')}
							className={cn('rounded-[3px] px-1.5 py-1', density === 'compact' ? 'bg-muted text-foreground' : 'text-foreground-subtle')}>
							<Rows4 size={13} />
						</button>
					</div>
					<button
						type="button"
						onClick={() => onRetry?.()}
						title={t('common.refresh')}
						className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
						<RefreshCw size={13} className={loading ? 'animate-spin' : undefined} />
					</button>
				</div>
			</div>

			{/* Table — horizontal scroll with the identity (first) + actions columns pinned. */}
			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				<table className="w-full table-fixed border-collapse text-left" style={{ minWidth: tableMinWidth }}>
					<colgroup>
						{visibleColumns.map(c => (
							<col key={c.key} style={colWidth(c.width)} />
						))}
						{hasRowActions && <col style={{ width: actionsWidth }} />}
					</colgroup>
					<thead>
						<tr className="border-b border-border bg-elevated">
							{visibleColumns.map((c, i) => {
								const sortable = Boolean(c.sortValue);
								const active = sort?.key === c.key;
								return (
									<th
										key={c.key}
										className={cn(
											'px-3 py-2 text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle',
											c.align === 'right' && 'text-right',
											c.align === 'center' && 'text-center',
											i === 0 && cn(stickyLeft, 'bg-elevated')
										)}>
										{sortable ? (
											<button
												type="button"
												onClick={() => cycleSort(c.key)}
												className={cn(
													'inline-flex items-center gap-1 hover:text-foreground',
													c.align === 'right' && 'flex-row-reverse',
													c.align === 'center' && 'mx-auto',
													active && 'text-foreground'
												)}>
												{toGreekCaps(c.header)}
												{active ? (
													sort?.dir === 'asc' ? (
														<ChevronUp size={12} />
													) : (
														<ChevronDown size={12} />
													)
												) : (
													<ChevronsUpDown size={12} className="text-foreground-disabled" />
												)}
											</button>
										) : (
											toGreekCaps(c.header)
										)}
									</th>
								);
							})}
							{hasRowActions && <th className={cn('px-3 py-2', stickyRight, 'bg-elevated')} />}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={colSpan} className="p-4">
									<Skeleton className="h-72 w-full rounded-md" />
								</td>
							</tr>
						) : total === 0 ? (
							<tr>
								<td colSpan={colSpan}>
									<div className="flex flex-col items-center gap-2 py-16 text-center">
										{emptyIcon}
										<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
									</div>
								</td>
							</tr>
						) : (
							pageRows.map(row => (
								<tr
									key={getRowId(row)}
									onClick={onRowClick ? () => onRowClick(row) : undefined}
									className={cn('group border-b border-border-subtle last:border-b-0 hover:bg-elevated', onRowClick && 'cursor-pointer')}>
									{visibleColumns.map((c, i) => (
										<td
											key={c.key}
											className={cn(
												'px-3 align-middle',
												cellPadY,
												c.align === 'right' && 'text-right',
												c.align === 'center' && 'text-center',
												i === 0 && cn(stickyLeft, 'bg-surface group-hover:bg-elevated')
											)}>
											<div className={cn('min-w-0', c.align === 'center' ? 'flex justify-center' : 'truncate')}>{c.render(row)}</div>
										</td>
									))}
									{hasRowActions && (
										<td
											className={cn('px-3', cellPadY, stickyRight, 'bg-surface group-hover:bg-elevated')}
											onClick={onRowClick ? e => e.stopPropagation() : undefined}>
											<span className="flex items-center justify-end gap-0.5">
												{rowActions?.(row)}
												{onEdit && (
													<Button variant="ghost" size="icon" aria-label={t('common.edit')} onClick={() => onEdit(row)}>
														<Pencil size={15} />
													</Button>
												)}
												{onDelete && (canDelete?.(row) ?? true) && (
													<Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => onDelete(row)}>
														<Trash2 size={15} className="text-danger-fg" />
													</Button>
												)}
											</span>
										</td>
									)}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Pager */}
			<div className="mt-1 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<Select
						value={String(pageSize)}
						onValueChange={v => {
							setPageSize(Number(v));
							setPageIndex(0);
						}}>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{PAGE_SIZES.map(size => (
								<SelectItem key={size} value={String(size)}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(0)}
						aria-label={t('system.grid.first')}>
						<ChevronsLeft size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(pageIndex - 1)}
						aria-label={t('system.grid.prev')}>
						<ChevronLeft size={15} />
					</Button>
					{pageNumbers(pageIndex, pageCount).map((p, i) =>
						p === 'gap' ? (
							<span key={`gap-${i}`} className="px-1 text-foreground-subtle">
								…
							</span>
						) : (
							<Button
								key={p}
								variant={p === pageIndex ? 'default' : 'outline'}
								size="icon"
								type="button"
								onClick={() => setPageIndex(p)}
								aria-current={p === pageIndex ? 'page' : undefined}>
								{p + 1}
							</Button>
						)
					)}
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(pageIndex + 1)}
						aria-label={t('system.grid.next')}>
						<ChevronRight size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(pageCount - 1)}
						aria-label={t('system.grid.last')}>
						<ChevronsRight size={15} />
					</Button>
				</div>
			</div>
		</div>
	);
}
